import { z } from 'zod';

export const STAGE1_ARTIFACT_STORAGE_KEY = 'seitiate-plus:research-stage1-artifact:v1';
export const STAGE1_ARTIFACT_STORAGE_EVENT = 'seitiate-plus:research-stage1-artifact-updated';

const scalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

const checksumSchema = z
  .string()
  .regex(/^[a-f0-9]{32,128}$/i, 'expected a hex checksum');

const stage1InputSchema = z
  .object({
    schema_version: z.string().optional(),
    dataset_name: z.string().min(1),
    source_path: z.string().optional(),
    source_sha256: checksumSchema,
    normalized_path: z.string().optional(),
    normalized_sha256: checksumSchema.optional(),
    delimiter: z.string().optional(),
    header_line_number: z.number().optional(),
    skipped_preamble_lines: z.number().optional(),
    row_count: z.number().nonnegative(),
    columns: z.array(z.string()),
    source_columns: z.record(z.string()).optional(),
    units: z.record(z.string()).optional(),
    warnings: z.array(z.string()).optional(),
    adapter: z.string().optional(),
  })
  .passthrough();

const stage1ValidationInputSchema = z
  .object({
    schema_version: z.string().optional(),
    label: z.string().min(1),
    method: z.string().optional(),
    source_path: z.string().optional(),
    workspace_path: z.string().optional(),
    source_sha256: checksumSchema.optional(),
    size_bytes: z.number().nonnegative().optional(),
  })
  .passthrough();

const analysisResultSchema = z
  .object({
    schema_version: z.string().optional(),
    analyzer: z.string().min(1),
    method: z.string().optional(),
    status: z.string().min(1),
    metrics: z.record(scalarSchema).default({}),
    flags: z.array(z.string()).default([]),
    input_path: z.string().optional(),
    input_sha256: checksumSchema.optional(),
    notes: z.string().optional(),
  })
  .passthrough();

const analysisRecordSchema = z
  .object({
    label: z.string().nullable().optional(),
    result: analysisResultSchema,
  })
  .passthrough();

const benchmarkCheckSchema = z
  .object({
    metric: z.string(),
    actual: scalarSchema.optional(),
    expected_min: scalarSchema.optional(),
    expected_max: scalarSchema.optional(),
    passed: z.boolean(),
  })
  .passthrough();

const benchmarkTaskSchema = z
  .object({
    name: z.string(),
    status: z.string().optional(),
    passed: z.boolean(),
    checks: z.array(benchmarkCheckSchema).default([]),
  })
  .passthrough();

const benchmarkSchema = z
  .object({
    schema_version: z.string().optional(),
    suite: z.string().optional(),
    passed: z.boolean(),
    passed_count: z.number().nonnegative(),
    total: z.number().nonnegative(),
    tasks: z.array(benchmarkTaskSchema).default([]),
  })
  .passthrough();

const scorecardGoalSchema = z
  .object({
    name: z.string(),
    direction: z.string().optional(),
    target: scalarSchema.optional(),
  })
  .passthrough();

const strategyGoalSchema = z
  .object({
    goal: z.string(),
    direction: z.string().optional(),
    target: scalarSchema.optional(),
    value: scalarSchema.optional(),
    // Nullable: the analyzer (comparison.py Goal.meets) emits null when a
    // strategy has no value for a goal or the value is non-numeric. The bundled
    // demo spec is all-boolean, but imported / custom-spec artifacts can be sparse.
    meets: z.boolean().nullable(),
  })
  .passthrough();

const strategySchema = z
  .object({
    strategy: z.string(),
    source: z.string().optional(),
    goals_met: z.number().nonnegative(),
    goals_assessed: z.number().nonnegative(),
    goals: z.array(strategyGoalSchema).default([]),
  })
  .passthrough();

const strategyScorecardSchema = z
  .object({
    schema_version: z.string().optional(),
    name: z.string().min(1).default('stage1-evidence'),
    note: z.string().optional(),
    goals: z.array(scorecardGoalSchema).default([]),
    strategies: z.array(strategySchema).default([]),
  })
  .passthrough();

const verificationSchema = z
  .object({
    passed: z.boolean(),
    score: z.number(),
    failed_categories: z.array(z.string()).default([]),
  })
  .passthrough();

const reviewerDecisionSchema = z
  .object({
    status: z.string().min(1).default('pending'),
    decision: z.string().nullable().optional(),
    notes: z.string().optional(),
  })
  .passthrough();

const reproducibilitySchema = z
  .object({
    replay_command: z.string().optional(),
    rerun_command_template: z.string().optional(),
  })
  .passthrough();

export const stage1SummarySchema = z
  .object({
    schema_version: z.literal('stage1-summary.v1'),
    run_id: z.string().min(1),
    run_dir: z.string().optional(),
    result_fingerprint: z
      .string()
      .regex(/^[a-f0-9]{64}$/i, 'expected a 64-character hex result fingerprint'),
    inputs: z.array(stage1InputSchema).min(1),
    validation_inputs: z.array(stage1ValidationInputSchema).default([]),
    analyses: z.array(analysisRecordSchema).default([]),
    benchmark: benchmarkSchema,
    strategy_scorecard: strategyScorecardSchema,
    verification: verificationSchema.nullable().optional(),
    caveats: z.array(z.string()).default([]),
    reviewer_decision: reviewerDecisionSchema.default({
      status: 'pending',
      decision: null,
      notes: 'The named reviewer records the final decision after reviewing real instrument evidence.',
    }),
    reproducibility: reproducibilitySchema.optional(),
  })
  .passthrough();

export type Stage1Summary = z.infer<typeof stage1SummarySchema>;
export type Stage1Input = Stage1Summary['inputs'][number];
export type Stage1ValidationInput = Stage1Summary['validation_inputs'][number];
export type Stage1Analysis = Stage1Summary['analyses'][number];
export type Stage1Strategy = Stage1Summary['strategy_scorecard']['strategies'][number];

export interface Stage1ArtifactBundle {
  summary: Stage1Summary;
  reportText?: string;
  summaryFileName?: string;
  reportFileName?: string;
  source: 'demo' | 'imported' | 'stored' | 'managed';
}

interface StoredStage1ArtifactBundle {
  summary: unknown;
  reportText?: string;
  summaryFileName?: string;
  reportFileName?: string;
  source?: Stage1ArtifactBundle['source'];
}

export function parseStage1Summary(value: unknown): Stage1Summary {
  return stage1SummarySchema.parse(value);
}

export function parseStage1SummaryText(text: string): Stage1Summary {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new Error(`Invalid JSON: ${error instanceof Error ? error.message : 'unable to parse file'}`);
  }
  return parseStage1Summary(parsed);
}

export function summarizeStage1ParseError(error: unknown): string {
  if (error instanceof z.ZodError) {
    const first = error.issues[0];
    if (!first) return 'Stage 1 summary did not match the expected schema.';
    const path = first.path.length > 0 ? first.path.join('.') : 'root';
    return `${path}: ${first.message}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Stage 1 summary could not be parsed.';
}

export function basenameForPath(path: string | null | undefined): string {
  if (!path) return 'not recorded';
  const normalized = path.replace(/\\/g, '/');
  const parts = normalized.split('/').filter(Boolean);
  return parts.at(-1) ?? path;
}

export function shortChecksum(value: string | null | undefined): string {
  if (!value) return 'not recorded';
  return value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-8)}` : value;
}

export function formatMetricName(name: string): string {
  return name.replace(/_/g, ' ');
}

export function formatMetricValue(value: unknown): string {
  if (value === null || value === undefined) return 'not recorded';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(6)));
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

export function formatStrategyName(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => (part.length <= 3 ? part.toUpperCase() : `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`))
    .join(' ');
}

export function countAnalysisFlags(summary: Stage1Summary): number {
  return summary.analyses.reduce((total, analysis) => total + (analysis.result.flags?.length ?? 0), 0);
}

export function redactedPath(path: string | null | undefined): string {
  if (!path) return 'not recorded';
  return `<local-path>/${basenameForPath(path)}`;
}

const WINDOWS_ABSOLUTE_PATH_PATTERN = /(^|[\s([{"'=`])((?:[A-Za-z]:\\|\\\\)[^\s`"'<>),;\]}]+)/g;
const WINDOWS_ABSOLUTE_PATH_PATTERN_WITH_SPACES = /(^|[\s([{"'=`])((?:[A-Za-z]:\\|\\\\)[^`"'<>),;\]}\r\n]+)/g;
const POSIX_ABSOLUTE_PATH_PATTERN = /(^|[\s([{"'=`])((?:\/(?!\/)[^\s`"'<>),;\]}]+)+)/g;
const POSIX_ABSOLUTE_PATH_PATTERN_WITH_SPACES = /(^|[\s([{"'=`])((?:\/(?!\/)[^`"'<>),;\]}\r\n]+)+)/g;
const FILE_URL_PATH_PATTERN = /(file:\/\/)(\/(?!\/)[^\s`"'<>),;\]}]+)/g;
const FILE_URL_PATH_PATTERN_WITH_SPACES = /(file:\/\/)(\/(?!\/)[^`"'<>),;\]}\r\n]+)/g;

function isAbsoluteLocalPath(value: string): boolean {
  const trimmed = value.trim();
  return /^(?:[A-Za-z]:\\|\\\\|\/(?!\/))/.test(trimmed);
}

function redactPathsInSegment(text: string, options: { allowSpaces?: boolean } = {}): string {
  const allowSpaces = options.allowSpaces ?? false;
  const windowsPattern = allowSpaces ? WINDOWS_ABSOLUTE_PATH_PATTERN_WITH_SPACES : WINDOWS_ABSOLUTE_PATH_PATTERN;
  const posixPattern = allowSpaces ? POSIX_ABSOLUTE_PATH_PATTERN_WITH_SPACES : POSIX_ABSOLUTE_PATH_PATTERN;
  const fileUrlPattern = allowSpaces ? FILE_URL_PATH_PATTERN_WITH_SPACES : FILE_URL_PATH_PATTERN;
  return text
    .replace(fileUrlPattern, (_match, prefix: string, path: string) => `${prefix}${redactedPath(path.trim())}`)
    .replace(windowsPattern, (_match, prefix: string, path: string) => `${prefix}${redactedPath(path.trim())}`)
    .replace(posixPattern, (_match, prefix: string, path: string) => `${prefix}${redactedPath(path.trim())}`);
}

export function redactLocalPathsInText(text: string): string {
  return text
    .split(/(`[^`]*`)/g)
    .map((segment) => redactPathsInSegment(segment, { allowSpaces: segment.startsWith('`') && segment.endsWith('`') }))
    .join('');
}

function redactLocalPathsInValue<T>(value: T): T {
  if (typeof value === 'string') {
    return (isAbsoluteLocalPath(value) ? redactedPath(value) : redactLocalPathsInText(value)) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactLocalPathsInValue(item)) as T;
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, redactLocalPathsInValue(item)]),
    ) as T;
  }
  return value;
}

export function redactStage1SummaryForStorage(summary: Stage1Summary): Stage1Summary {
  return parseStage1Summary(redactLocalPathsInValue(summary));
}

export function loadStoredStage1ArtifactBundle(): Stage1ArtifactBundle | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STAGE1_ARTIFACT_STORAGE_KEY);
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as StoredStage1ArtifactBundle;
    return {
      summary: parseStage1Summary(stored.summary),
      reportText: stored.reportText,
      summaryFileName: stored.summaryFileName,
      reportFileName: stored.reportFileName,
      source: stored.source === 'managed' ? 'managed' : 'stored',
    };
  } catch {
    window.localStorage.removeItem(STAGE1_ARTIFACT_STORAGE_KEY);
    return null;
  }
}

export function saveStage1ArtifactBundle(bundle: Stage1ArtifactBundle): void {
  if (typeof window === 'undefined') return;
  const stored: StoredStage1ArtifactBundle = {
    summary: redactStage1SummaryForStorage(bundle.summary),
    reportText: bundle.reportText ? redactLocalPathsInText(bundle.reportText) : undefined,
    summaryFileName: bundle.summaryFileName,
    reportFileName: bundle.reportFileName,
    source: bundle.source,
  };
  window.localStorage.setItem(STAGE1_ARTIFACT_STORAGE_KEY, JSON.stringify(stored));
  window.dispatchEvent(new CustomEvent(STAGE1_ARTIFACT_STORAGE_EVENT));
}

export function clearStoredStage1ArtifactBundle(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STAGE1_ARTIFACT_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(STAGE1_ARTIFACT_STORAGE_EVENT));
}
